import { useState } from "react";
import {
  IonCard, IonGrid, IonRow, IonCol, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
  IonIcon, IonList, IonItem, IonLabel, IonSegment, IonSegmentButton, IonCardHeader, IonSkeletonText,
} from "@ionic/react";
import { chevronBackOutline, ellipsisVerticalOutline, callOutline, mailOutline, chatboxOutline, sunnyOutline, moonOutline } from "ionicons/icons";
import { brandVars, brandWavesVars } from "@/utils/brandColor";
import { appThemeDark } from "@/utils/appTheme";

// The two previews under a Primary Color row: an app screen with the colour on
// its bar, tabs and icons, and a sign-in page whose wave background is built
// from tints of it. Shared by the admin's Preferences → Branding and the /app
// Branding page so the two always show the same thing.
//
// props: color (hex), logoArt (the node drawn in the logo circle)

const Lines = ({ widths = ["90%", "65%", "75%"] }) => widths.map((w, i) => <IonSkeletonText animated={true} key={i} style={{ width: w }} />);

export default function BrandingPreview({ color, logoArt, scope = "app" }) {
  // The sun/moon above the business card flips BOTH previews between the
  // light and dark palettes, the way the real toggle flips the app. On the
  // ADMIN branding page (scope="admin") it is the admin's own theme toggle:
  // it persists adminDarkMode and AdminLayout follows along.
  const [dark, setDark] = useState(() => {
    try { return scope === "admin" ? localStorage.getItem("adminDarkMode") === "true" : appThemeDark(); } catch { return false; }
  });
  // the toggle is the real thing: it flips the whole theme with the preview
  const applyDark = (d) => {
    setDark(d);
    try { localStorage.setItem(scope === "admin" ? "adminDarkMode" : "appDarkMode", String(d)); } catch { /* preview still flips */ }
    document.body.classList.toggle("dark", d);
    window.dispatchEvent(new CustomEvent("mintslip-theme-change", { detail: { dark: d, scope } }));
  };
  const vars = brandVars(color);
  // The sign-in backdrop: the stacked waves in the colour and its tints,
  // exactly what the real signing page wears.
  Object.assign(vars, brandWavesVars(color));
  return (
      <IonGrid className={`preview-grid${dark ? " preview-dark" : ""}`}>
        <IonRow className="ion-align-items-stretch">
          {/* an app screen: toolbar + tabs in the colour, primary icons in the lists */}
          <IonCol size="12" sizeLg="">
            <IonCard className="preview-card">
              <div className="preview-content" style={vars}>
                <IonHeader className="preview-header">
                  <IonToolbar color="primary" className="no-padding-top">
                    <IonButtons slot="start"><IonButton><IonIcon slot="icon-only" icon={chevronBackOutline} /></IonButton></IonButtons>
                    <IonTitle><IonSkeletonText animated={true} style={{ width: 100 }} /></IonTitle>
                    <IonButtons slot="end"><IonButton><IonIcon slot="icon-only" icon={ellipsisVerticalOutline} /></IonButton></IonButtons>
                  </IonToolbar>
                  <IonToolbar color="primary" className="toolbar-segment">
                    <IonSegment value="call" scrollable>
                      {["call", "email", "text", "notes", "files"].map((v) => (
                        <IonSegmentButton key={v} value={v}><IonLabel><IonSkeletonText animated={true} style={{ width: 50 }} /></IonLabel></IonSegmentButton>
                      ))}
                    </IonSegment>
                  </IonToolbar>
                </IonHeader>
                <IonCard>
                  <IonList>
                    {[callOutline, mailOutline, chatboxOutline].map((icon, i) => (
                      <IonItem key={i}><IonIcon color="primary" slot="start" icon={icon} /><IonLabel><Lines /></IonLabel></IonItem>
                    ))}
                  </IonList>
                </IonCard>
                <IonCard>
                  <IonToolbar className="toolbar-segment">
                    <IonSegment value="call" scrollable>
                      {[["call", callOutline], ["email", mailOutline], ["text", chatboxOutline]].map(([v, icon]) => (
                        <IonSegmentButton key={v} value={v}><IonIcon icon={icon} /><IonLabel><IonSkeletonText animated={true} style={{ width: 50 }} /></IonLabel></IonSegmentButton>
                      ))}
                    </IonSegment>
                  </IonToolbar>
                </IonCard>
                <IonCard>
                  <IonList>
                    {[callOutline, mailOutline, chatboxOutline].map((icon, i) => (
                      <IonItem key={i}><IonLabel><Lines /></IonLabel><IonIcon slot="end" color="primary" icon={icon} /></IonItem>
                    ))}
                  </IonList>
                </IonCard>
              </div>
            </IonCard>
          </IonCol>
          {/* the sign-in page: the wave background in tints of the colour, the business card with the logo */}
          <IonCol size="12" sizeLg="">
            <IonCard className="preview-card">
              <div className="unauthorized-content preview-content" style={vars}>
                <div className="abstract-bg" aria-hidden="true" />
                <div className="container">
                  {/* the logo circle sits outside the card so nothing clips it: it overlaps the card's top edge, above the wash */}
                  <div className="business-card-wrap">
                    <div className="theme-toggle-row">
                      <IonButton fill="clear" className="theme-toggle" aria-label={dark ? "Light mode" : "Dark mode"} onClick={() => applyDark(!dark)}>
                        <IonIcon slot="icon-only" icon={dark ? sunnyOutline : moonOutline} />
                      </IonButton>
                    </div>
                    <div className="business-logo"><div className="round">{logoArt}</div></div>
                    <IonCard className="business-card">
                      <IonCardHeader>
                        <IonLabel>
                          <IonSkeletonText animated={true} style={{ width: "65%", margin: "4px auto" }} />
                          <IonSkeletonText animated={true} style={{ width: "70%", margin: "4px auto" }} />
                        </IonLabel>
                        <div className="ion-padding-top" />
                        <IonList lines="none">
                          {[0, 1, 2, 3].map((i) => <IonItem key={i}><IonLabel><Lines /></IonLabel></IonItem>)}
                        </IonList>
                      </IonCardHeader>
                    </IonCard>
                  </div>
                </div>
              </div>
            </IonCard>
          </IonCol>
        </IonRow>
      </IonGrid>
  );
}
